if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        // Quantity limits from product metafields (custom.min / custom.max).
        this.productId = Number(this.dataset.productId) || null;
        this.minQty = Number(this.dataset.minQty) || null;
        this.maxQty = Number(this.dataset.maxQty) || null;
        this.qtyInput = this.form.querySelector('[name="quantity"]');

        if ((this.minQty || this.maxQty) && this.productId) {
          this.remaining = this.maxQty;
          this.refreshQtyLimit();
          // Clamp live as the shopper types or steps, so we never rely on the native
          // min/max attributes (which trigger ugly browser/app validation popups).
          if (this.qtyInput) {
            this.qtyInput.addEventListener('change', this.handleQtyChange.bind(this));
          }
          // Re-evaluate the remaining limit whenever the cart changes elsewhere on the page.
          const refresh = this.refreshQtyLimit.bind(this);
          document.addEventListener('on:cart:add', refresh);
          document.addEventListener('on:line-item:change', refresh);
        }

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Returns the total quantity of this product (across all variants) already in the cart.
     * @returns {Promise<number>}
     */
    async getCartQtyForProduct() {
      try {
        const response = await fetch(`${theme.routes.cart}.js`);
        if (!response.ok) return 0;
        const cart = await response.json();
        return cart.items
          .filter((item) => item.product_id === this.productId)
          .reduce((total, item) => total + item.quantity, 0);
      } catch (error) {
        console.log(error); // eslint-disable-line
        return 0;
      }
    }

    /**
     * Recomputes how many more units may be added (max minus what is already in the cart) and
     * stores it for live clamping. Does not touch native min/max attributes on purpose.
     */
    async refreshQtyLimit() {
      if (!this.maxQty || !this.qtyInput) return;
      const inCart = await this.getCartQtyForProduct();
      this.remaining = Math.max(this.maxQty - inCart, 0);
      if (Number(this.qtyInput.value) > this.remaining) {
        // Keep at least 1 so the input never goes invalid against its native min="1".
        this.qtyInput.value = Math.max(this.remaining, 1);
      }
    }

    /**
     * Live-clamps the quantity input to the product's min and remaining-max as the shopper edits.
     * Snaps silently — the standing min/max info line under the button explains the limits, and
     * we deliberately avoid native min/max attributes so no browser/app validation popup appears.
     */
    handleQtyChange() {
      if (!this.qtyInput) return;
      let val = parseInt(this.qtyInput.value, 10);
      if (Number.isNaN(val) || val <= 0) return;

      if (this.minQty && val < this.minQty) val = this.minQty;
      if (this.maxQty && this.remaining != null && val > this.remaining) {
        val = Math.max(this.remaining, 1);
      }

      if (String(val) !== this.qtyInput.value) this.qtyInput.value = val;
    }

    /**
     * Clamps the requested quantity to the product's min/max limits, accounting for what is
     * already in the cart. Shows a message and returns null when nothing more can be added.
     * @returns {Promise<number|null>} Quantity to add, or null to abort.
     */
    async resolveLimitedQuantity() {
      if ((!this.minQty && !this.maxQty) || !this.productId || !this.qtyInput) return undefined;

      let requested = parseInt(this.qtyInput.value, 10) || 0;

      if (this.minQty && requested < this.minQty) {
        requested = this.minQty;
        this.setErrorMsgState(theme.strings.qtyMinLimit.replace('[min]', this.minQty));
      }

      if (this.maxQty) {
        const inCart = await this.getCartQtyForProduct();
        const remaining = Math.max(this.maxQty - inCart, 0);
        if (requested > remaining) {
          requested = remaining;
          this.setErrorMsgState(theme.strings.qtyMaxLimit.replace('[max]', this.maxQty));
        }
        if (remaining <= 0) return null;
      }

      this.qtyInput.value = requested;
      return requested;
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Enforce quantity limits from product metafields. Aborts when the cart is already full.
      const limitedQty = await this.resolveLimitedQuantity();
      if (limitedQty === null) return;

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg.textContent
          }
        }));
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
